import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Users,
  ListChecks,
  LayoutDashboard,
  SlidersHorizontal,
  Upload,
} from "lucide-react";
import PhoneSelector from "../../components/PhoneSelector";
import { usePhoneAccount } from "../../contexts/PhoneAccountContext";

import ContactsDashboardPage from "./ContactsDashboardPage";
import ContactsListPage from "./ContactsListPage";
import ContactListsPage from "./ContactListsPage";
import CustomFieldsPage from "./CustomFieldsPage";
import ImportsPage from "./ImportsPage";

/**
 * ContactsHub
 *
 * Top-level container for the centralized contacts module. Tabs are
 * driven by ?tab= so deep links and back/forward work.
 */
const TABS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, Comp: ContactsDashboardPage },
  { id: "contacts", label: "Contacts", icon: Users, Comp: ContactsListPage },
  { id: "lists", label: "Lists", icon: ListChecks, Comp: ContactListsPage },
  { id: "custom-fields", label: "Custom Fields", icon: SlidersHorizontal, Comp: CustomFieldsPage },
  { id: "imports", label: "Imports", icon: Upload, Comp: ImportsPage },
];

const ContactsHub = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedPhoneId } = usePhoneAccount();
  const params = new URLSearchParams(location.search);
  const initial = params.get("tab") || "dashboard";
  const [activeTab, setActiveTab] = useState(
    TABS.some((t) => t.id === initial) ? initial : "dashboard"
  );

  useEffect(() => {
    const next = new URLSearchParams(location.search);
    if (next.get("tab") !== activeTab) {
      next.set("tab", activeTab);
      navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const ActiveComp = TABS.find((t) => t.id === activeTab)?.Comp;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Contacts</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Manage your centralized contact universe — per WhatsApp phone.
              </p>
            </div>
            <div className="w-full sm:w-auto">
              <PhoneSelector compact section="contacts_management" />
            </div>
          </div>

          <div className="mt-5 -mb-px flex gap-1 overflow-x-auto">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const active = tab.id === activeTab;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors whitespace-nowrap ${
                    active
                      ? "border-emerald-600 text-emerald-700 bg-emerald-50/40"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <Icon size={16} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {!selectedPhoneId ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10 text-center">
            <h3 className="text-lg font-semibold text-gray-900">Select a WhatsApp phone</h3>
            <p className="text-sm text-gray-500 mt-1">
              Each WhatsApp phone has its own contact universe. Pick one to continue.
            </p>
          </div>
        ) : (
          ActiveComp && <ActiveComp phoneId={selectedPhoneId} setActiveTab={setActiveTab} />
        )}
      </div>
    </div>
  );
};

export default ContactsHub;
